import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  PageContainer,
  Progress,
  StatutEvaluationBadge,
} from "@repo/ui";
import { Breadcrumb } from "@repo/ui";
import { EnvoyerLienParcoursDialog, useEnvoiLienParcours } from "@repo/campagne";
import { useMutation, useQuery } from "convex/react";
import {
  ArrowLeft,
  Check,
  Copy,
  ExternalLink,
  FileText,
  Inbox,
  Loader2,
  Mail,
  RefreshCw,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { Link, useParams } from "react-router";
import { ConfirmerSuppression, type CibleSuppression } from "../../components/ConfirmerSuppression";
import { useState } from "react";
import { getEvaluationTitle, libelleComposition } from "@repo/utils";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { arianeOrganisation, racineOrganisation } from "../../lib/ariane";
import { StatutGlobalEnum, TypeEvalEnum } from "@convex/schema";
import { dateCourteHeure } from "@repo/utils";

/** Id Convex de l'évaluation, affiché pour le support : sert à retrouver le
    document dans le dashboard Convex ou à le passer aux mutations de relance. */
function EvaluationIdBadge({ evalId }: { evalId: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <span className="font-mono select-all">{evalId}</span>
      <button
        type="button"
        title="Copier l'id de l'évaluation"
        aria-label="Copier l'id de l'évaluation"
        className="hover:text-foreground transition-colors"
        onClick={() => {
          void navigator.clipboard.writeText(evalId);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-green-600" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </button>
    </div>
  );
}

function getEvaluationIcon(typeEval: string) {
  if (typeEval === TypeEvalEnum.INBASKET)
    return <Inbox className="h-5 w-5 text-muted-foreground" />;
  return <FileText className="h-5 w-5 text-muted-foreground" />;
}

export function ParcoursDetailAdmin() {
  const { id } = useParams<{ id: string }>();

  const [relancingId, setRelancingId] = useState<string | null>(null);
  const [relanceConfirmOpen, setRelanceConfirmOpen] = useState(false);
  const [relancePending, setRelancePending] = useState<{
    id: string;
    handler: (id: string) => Promise<void>;
    isTermine: boolean;
  } | null>(null);

  const relancerAnalyseInbasket = useMutation(
    api.evaluations.relancerAnalyseInbasketSuperAdmin,
  );
  const relancerAnalysePami = useMutation(
    api.evaluations.relancerAnalysePamiSuperAdmin,
  );
  const relancerAnalyseEntStruct = useMutation(
    api.evaluations.relancerAnalyseEntStructSuperAdmin,
  );
  const relancerNotationsEnErreur = useMutation(
    api.evaluations.relancerNotationsEnErreur,
  );

  const result = useQuery(
    api.superAdmin.obtenirDetailParcoursSuperAdmin,
    id ? { parcoursId: id as Id<"parcoursEvaluations"> } : "skip",
  );
  const envoiLien = useEnvoiLienParcours({
    parcoursId: id as Id<"parcoursEvaluations"> | undefined,
    emailCandidat: result?.parcours.emailCandidat,
    superAdmin: true,
  });
  const restaurer = useMutation(api.corbeille.restaurer);
  const [aSupprimer, setASupprimer] = useState<{
    cible: CibleSuppression;
    libelle: string;
  } | null>(null);

  const handleRestaurer = (cible: CibleSuppression) => {
    restaurer({ cible }).catch((err: unknown) =>
      alert(err instanceof Error ? err.message : "Erreur inconnue"),
    );
  };

  const handleRelancerInbasket = async (evaluationId: string) => {
    setRelancingId(evaluationId);
    try {
      await relancerAnalyseInbasket({
        evaluationId: evaluationId as Id<"evaluations">,
      });
    } finally {
      setRelancingId(null);
    }
  };

  const handleRelancerPami = async (evaluationId: string) => {
    setRelancingId(evaluationId);
    try {
      await relancerAnalysePami({
        evaluationId: evaluationId as Id<"evaluations">,
      });
    } finally {
      setRelancingId(null);
    }
  };

  const handleRelancerEntStruct = async (evaluationId: string) => {
    setRelancingId(evaluationId);
    try {
      await relancerAnalyseEntStruct({
        evaluationId: evaluationId as Id<"evaluations">,
      });
    } finally {
      setRelancingId(null);
    }
  };

  // Relance ciblée : un SEUL appel LLM pour tous les modèles en erreur, les
  // notations déjà terminées ne sont pas rejouées.
  const handleRelancerNotationsEnErreur = async (evaluationId: string) => {
    setRelancingId(evaluationId);
    try {
      await relancerNotationsEnErreur({
        evaluationId: evaluationId as Id<"evaluations">,
      });
    } finally {
      setRelancingId(null);
    }
  };

  const getRelancerHandler = (evaluation: {
    typeEval: string;
    template?: string;
  }): ((id: string) => Promise<void>) | null => {
    if (evaluation.typeEval === TypeEvalEnum.INBASKET)
      return handleRelancerInbasket;
    if (
      evaluation.typeEval === TypeEvalEnum.FORM &&
      evaluation.template === "pami"
    )
      return handleRelancerPami;
    if (evaluation.typeEval === TypeEvalEnum.ENT_STRUCT)
      return handleRelancerEntStruct;
    return null;
  };

  const openRelanceConfirm = (
    evalId: string,
    handler: (id: string) => Promise<void>,
    isTermine: boolean,
  ) => {
    setRelancePending({ id: evalId, handler, isTermine });
    setRelanceConfirmOpen(true);
  };

  const confirmRelance = async () => {
    if (!relancePending) return;
    setRelanceConfirmOpen(false);
    await relancePending.handler(relancePending.id);
    setRelancePending(null);
  };

  const cancelRelance = () => {
    setRelanceConfirmOpen(false);
    setRelancePending(null);
  };

  if (result === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (result === null) {
    return (
      <PageContainer>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive">
            Parcours introuvable
          </h1>
          <Link
            to="/"
            className="mt-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour aux organisations
          </Link>
        </div>
      </PageContainer>
    );
  }

  const { parcours, evaluations, progression } = result;

  const nom = parcours.anonyme
    ? "Anonyme"
    : [parcours.prenomCandidat, parcours.nomCandidat].filter(Boolean).join(" ") ||
      parcours.emailCandidat ||
      "—";

  return (
    <PageContainer>
      <Breadcrumb
        className="mb-6"
        items={[
          ...arianeOrganisation(parcours.organisationId, parcours.organisationNom, "campagnes"),
          // Parcours hors campagne : on remonte à la vue « hors campagne » de
          // l'organisation, pas à une campagne qui n'existe pas.
          parcours.campagneId
            ? {
                label: parcours.campagneNom ?? "Campagne",
                href: `/campagnes/${parcours.campagneId}`,
              }
            : {
                label: "Hors campagne",
                href: `${racineOrganisation(parcours.organisationId)}/hors-campagne`,
              },
          { label: nom },
        ]}
      />

      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="text-2xl">{nom}</CardTitle>
              <CardDescription>{parcours.titre}</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {parcours.archive && <Badge variant="secondary">Archivé</Badge>}
              {parcours.deletedAt !== undefined ? (
                <>
                  <Badge variant="destructive">Supprimé</Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRestaurer({ kind: "parcours", id: parcours._id })}
                  >
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Restaurer
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={envoiLien.ouvrir}
                    disabled={parcours.anonyme === true || !parcours.emailCandidat}
                  >
                    <Mail className="h-4 w-4 mr-1" />
                    {parcours.lienEnvoye ? "Renvoyer le lien" : "Envoyer le lien"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive border-destructive/30"
                    onClick={() =>
                      setASupprimer({
                        cible: { kind: "parcours", id: parcours._id },
                        libelle: `Le parcours de ${nom}`,
                      })
                    }
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Supprimer
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-muted-foreground">Email</dt>
              <dd>{parcours.emailCandidat ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Épreuves</dt>
              <dd>{libelleComposition(evaluations) || "—"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Organisation</dt>
              <dd>{parcours.organisationNom ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Campagne</dt>
              <dd>{parcours.campagneNom ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Lien</dt>
              <dd>
                {parcours.lienEnvoye ? (
                  <Badge variant="success">Envoyé</Badge>
                ) : (
                  <Badge variant="outline">Non envoyé</Badge>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Créé le</dt>
              <dd>{dateCourteHeure(parcours.dateCreation)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Évaluations</CardTitle>
              <CardDescription>Liste des évaluations du parcours</CardDescription>
            </div>
            <div className="flex items-center gap-2 min-w-[140px]">
              <Progress
                value={progression.pourcentage}
                className={`h-2 flex-1 ${progression.pourcentage === 100 ? "[&>div]:bg-green-500" : ""}`}
              />
              <span
                className={`text-sm font-medium whitespace-nowrap ${progression.pourcentage === 100 ? "text-green-600" : ""}`}
              >
                {progression.pourcentage}%
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {evaluations.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Aucune évaluation
              </p>
            ) : (
              evaluations.map((evaluation) => (
                <div
                  key={evaluation.evalId}
                  className="flex flex-col gap-3 p-4 border border-charte-line rounded-surface transition-colors duration-150 hover:border-charte-line-accent"
                >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-muted rounded-controle">
                      {getEvaluationIcon(evaluation.typeEval)}
                    </div>
                    <div>
                      <h3 className="font-medium">
                        {getEvaluationTitle(evaluation)}
                      </h3>
                      <EvaluationIdBadge evalId={evaluation.evalId} />
                      {evaluation.dateDebut && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Commencé le {dateCourteHeure(evaluation.dateDebut)}
                          {evaluation.dateFin &&
                            ` - Terminé le ${dateCourteHeure(evaluation.dateFin)}`}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <StatutEvaluationBadge
                      statut={evaluation.statut}
                      detailAnalyse
                    />
                    {evaluation.statut === StatutGlobalEnum.TERMINE && (
                      <Link
                        to={`/evaluations/${evaluation.evalId}?from=/parcours/${id}`}
                      >
                        <Button variant="outline" size="sm">
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Voir les résultats
                        </Button>
                      </Link>
                    )}
                    {(() => {
                      const handler = getRelancerHandler(evaluation);
                      const isTermine =
                        evaluation.statut === StatutGlobalEnum.TERMINE;
                      const showButton =
                        handler !== null &&
                        evaluation.statut !== StatutGlobalEnum.NON_COMMENCE;
                      if (!showButton) return null;
                      return (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            openRelanceConfirm(
                              evaluation.evalId,
                              handler,
                              isTermine,
                            )
                          }
                          disabled={relancingId === evaluation.evalId}
                          title={
                            !isTermine
                              ? "Déclencher l'analyse LLM même si l'évaluation n'est pas terminée"
                              : undefined
                          }
                        >
                          {relancingId === evaluation.evalId ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4 mr-1" />
                          )}
                          {isTermine ? "Relancer l'analyse" : "Lancer l'analyse"}
                        </Button>
                      );
                    })()}
                  </div>
                </div>

                  {/* Tolérance zéro (P4) : une seule notation en ERREUR met
                      l'éval en ERREUR_ANALYSE et masque TOUS les résultats. On
                      affiche le modèle + le message de la dernière tentative :
                      « modèle non disponible » se corrige dans config.ts,
                      « service indisponible » par un simple clic sur relancer. */}
                  {evaluation.notationsEnErreur.length > 0 && (
                    <div className="rounded-surface border border-destructive/40 bg-destructive/5 p-3">
                      <p className="text-xs font-medium text-destructive">
                        {evaluation.notationsEnErreur.length} notation
                        {evaluation.notationsEnErreur.length > 1 ? "s" : ""} en
                        erreur — aucun résultat n'est exposé tant qu'elles ne
                        sont pas reprises.
                      </p>
                      <ul className="mt-2 space-y-1">
                        {evaluation.notationsEnErreur.map((n) => (
                          <li
                            key={n.notationId}
                            className="text-xs text-muted-foreground"
                          >
                            <span className="font-mono">{n.model ?? "—"}</span>
                            {" : "}
                            {n.erreur ?? "cause non renseignée"}
                          </li>
                        ))}
                      </ul>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={() =>
                          handleRelancerNotationsEnErreur(evaluation.evalId)
                        }
                        disabled={relancingId === evaluation.evalId}
                      >
                        {relancingId === evaluation.evalId ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4 mr-1" />
                        )}
                        Relancer les {evaluation.notationsEnErreur.length}{" "}
                        notation
                        {evaluation.notationsEnErreur.length > 1 ? "s" : ""} en
                        erreur
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      <EnvoyerLienParcoursDialog
        envoi={envoiLien}
        emailCandidat={parcours.emailCandidat}
        lienEnvoye={parcours.lienEnvoye}
      />

      <ConfirmerSuppression
        cible={aSupprimer?.cible ?? null}
        libelle={aSupprimer?.libelle ?? ""}
        onClose={() => setASupprimer(null)}
      />

      <Dialog
        isOpen={relanceConfirmOpen}
        onClose={cancelRelance}
        title={relancePending?.isTermine ? "Relancer l'analyse ?" : "Lancer l'analyse ?"}
        className="max-w-sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {relancePending?.isTermine
              ? "L'analyse existante sera écrasée et recalculée depuis zéro."
              : "L'analyse LLM sera déclenchée même si l'évaluation n'est pas terminée."}
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={cancelRelance}>
              Annuler
            </Button>
            <Button onClick={confirmRelance}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Confirmer
            </Button>
          </div>
        </div>
      </Dialog>
    </PageContainer>
  );
}
